import type { WidgetConfig } from './types';

const SCRIPT =
  typeof document !== 'undefined'
    ? (document.currentScript as HTMLScriptElement | null)
    : null;

const SCRIPT_SRC =
  typeof document !== 'undefined'
    ? (document.currentScript as HTMLScriptElement | null)?.src || ''
    : '';

function isLocalhostUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
  } catch {
    return false;
  }
}

function resolveDefaultEndpoint(): string {
  if (SCRIPT_SRC && isLocalhostUrl(SCRIPT_SRC)) {
    return 'http://localhost:3001';
  }

  return 'https://api.fluxbotia.com';
}

function buildDefaultConfig(): WidgetConfig {
  return {
    token: '',
    endpoint: resolveDefaultEndpoint(),
    apiVersion: 'v1',
    position: 'bottom-right',
    primaryColor: '#2563eb',
    greeting: 'Hola! ¿Cómo podemos ayudarte?',
    locale: 'es',
    tenantMode: false,
    clientVersion: '1.0.0',
  };
}

export function getDefaults(): WidgetConfig {
  return buildDefaultConfig();
}

export function readDataAttributes(): Partial<WidgetConfig> {
  if (!SCRIPT) {
    return {};
  }

  const defaults = buildDefaultConfig();

  return {
    token: SCRIPT.dataset.token,
    endpoint: SCRIPT.dataset.endpoint,
    apiVersion: SCRIPT.dataset.apiVersion,
    position: (SCRIPT.dataset.position as WidgetConfig['position']) || defaults.position,
    primaryColor: SCRIPT.dataset.primaryColor,
    greeting: SCRIPT.dataset.greeting,
    locale: SCRIPT.dataset.locale,
    tenantMode: SCRIPT.dataset.tenantMode === 'true',
    clientVersion: SCRIPT.dataset.clientVersion,
  };
}

export function validateConfig(config: WidgetConfig): boolean {
  if (!config.token || config.token.trim().length === 0) {
    console.error('WidgetConfig Error: token is required');
    return false;
  }
  if (!config.token.startsWith('fbw_live_')) {
    console.error('WidgetConfig Error: token must start with fbw_live_');
    return false;
  }

  if (!config.endpoint || config.endpoint.trim().length === 0) {
    console.error('WidgetConfig Error: endpoint is required');
    return false;
  }
  try {
    const endpoint = new URL(config.endpoint);
    const localhost =
      endpoint.hostname === 'localhost' || endpoint.hostname === '127.0.0.1';
    if (endpoint.protocol !== 'https:' && !localhost) {
      console.error('WidgetConfig Error: endpoint must use https');
      return false;
    }
  } catch {
    console.error('WidgetConfig Error: endpoint is not a valid URL');
    return false;
  }

  if (!['bottom-right', 'bottom-left', 'top-right', 'top-left'].includes(config.position)) {
    console.error('WidgetConfig Error: invalid position');
    return false;
  }

  return true;
}

export function buildConfig(dataAttrs: Partial<WidgetConfig>): WidgetConfig {
  const config = { ...buildDefaultConfig(), ...dataAttrs } as WidgetConfig;

  if (typeof console !== 'undefined' && typeof console.info === 'function') {
    let endpointOrigin = config.endpoint;
    try {
      endpointOrigin = new URL(config.endpoint).origin;
    } catch {
      // Keep the raw endpoint when it cannot be parsed.
    }

    console.info('[FluxBot Widget] API target resolved', {
      endpoint: config.endpoint,
      endpointOrigin,
      apiVersion: config.apiVersion,
      scriptSrc: SCRIPT_SRC || null,
      localDev: isLocalhostUrl(config.endpoint),
    });
  }

  return config;
}
